import {
  CLOCK_PROBES,
  CLOCK_PROBE_GAP_MS,
  ClientEvent,
  ServerEvent,
  medianClockOffset,
} from '@anisync/types';
import { useCallback, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';

/**
 * Różnica między zegarem serwera i przeglądarki.
 *
 * Bez tej korekty synchronizacja rozjeżdżałaby się o tyle, o ile spieszy się
 * zegar systemowy widza — a to potrafią być minuty, całkowicie niezależnie od
 * jakości połączenia. Nie ma tu nic do naprawienia po stronie sieci: to błąd
 * zegara, więc trzeba go zmierzyć i odjąć.
 *
 * Mierzymy `CLOCK_PROBES` razy i bierzemy **medianę**, bo pojedyncza sonda
 * potrafi trafić na zacięcie i zawyżyć wynik o cały czas oczekiwania.
 * Pomiar powtarzamy po każdym ponownym połączeniu — po dłuższej przerwie
 * poprzedni wynik jest już tylko historią.
 */
export function useServerClockOffset(socket: Socket | null): {
  offsetMs: number;
  measure: () => void;
} {
  const [offsetMs, setOffsetMs] = useState(0);

  const measure = useCallback(() => {
    if (socket === null) return;

    const samples: Array<{ clientNow: number; serverNow: number; receivedAt: number }> = [];

    const onPong = (payload: { clientNow: number; serverNow: number }): void => {
      samples.push({
        clientNow: payload.clientNow,
        serverNow: payload.serverNow,
        receivedAt: Date.now(),
      });
      if (samples.length >= CLOCK_PROBES) {
        socket.off(ServerEvent.TimePong, onPong);
        setOffsetMs(medianClockOffset(samples));
      }
    };

    socket.on(ServerEvent.TimePong, onPong);

    for (let i = 0; i < CLOCK_PROBES; i += 1) {
      // Odstęp między sondami jest istotny: wysłane naraz trafiłyby w to samo
      // zacięcie i mediana z trzech identycznych błędów nic by nie naprawiła.
      setTimeout(() => socket.emit(ClientEvent.TimePing, Date.now()), i * CLOCK_PROBE_GAP_MS);
    }
  }, [socket]);

  useEffect(() => {
    if (socket === null) return;

    const onConnect = (): void => measure();
    socket.on('connect', onConnect);
    if (socket.connected) measure();

    return () => {
      socket.off('connect', onConnect);
    };
  }, [socket, measure]);

  return { offsetMs, measure };
}
